import React, { useMemo } from "react";
import { Typography, Button, Grid, Box, darken, alpha, useTheme, lighten } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";

import { handleDownloadDocsFiles } from "../../../services/documents/document-files-api.service";

import { DocumentsReportPeriodDTO } from "../../../models/documents/documents-reports/documents-report-period-dto";

import useAuthorization from "../../../custom-hooks/useAuthorization/useAuthorization";

import { AUTHORIZATIONS } from "../../../utils/constants/authorization-constants";

import { getPeriodIds, hasPeriodIds, PERIOD_CELL_ROW, TABLE_CELL } from "./utils";

import { Props } from "./interfaces";
import { HeaderCellContent } from "./styles";

import { COLORS } from "src/_globalStore/redux/reducers/Theme/themeConstants";

const DocumentsPeriod = ({ items, dynamicColumn }: Props) => {
  const theme = useTheme();

  const allowDownload = useAuthorization([AUTHORIZATIONS.LegalEntityAccess.DownloadDocuments]);

  const isSysAdmin = useAuthorization([AUTHORIZATIONS.SystemAccess.CompanyAccountsConfig]);

  const docsByPeriod = useMemo(() => {
    let result: DocumentsReportPeriodDTO[] = [];

    let found = false;

    for (let i = 0; i < items.length; i++) {
      if (found) break;

      let reportCategoryGroups = items[i].reportCategoryGroups || [];

      if (reportCategoryGroups) {
        for (let j = 0; j < reportCategoryGroups.length; j++) {
          let docsByPeriod = reportCategoryGroups[j].docsByPeriod;
          if (docsByPeriod && docsByPeriod.length) {
            result = docsByPeriod;
            found = true;
            break;
          } else {
            result = reportCategoryGroups[j]?.documentsReportPeriodAndCurrency?.[0]?.docsByPeriod ?? [];
            found = true;
          }
        }
      }
    }

    return result;
  }, [items]);

  return (
    <>
      {!!docsByPeriod.length && (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            borderBottom: `1px solid ${lighten(theme.palette.secondary.light, 0.6)}`,

            [`& .${TABLE_CELL}`]: {
              borderRight: `1px solid ${darken(COLORS.black_gradients.greyLight, 0.2)}`,
              backgroundColor: alpha(theme.palette.primary.main, 0.14),
            },

            "&>div": {
              "&>div": {
                "&:first-of-type": {
                  borderTopLeftRadius: "15px",
                },
                "&:last-of-type": {
                  borderRight: `0px`,
                  borderTopRightRadius: "15px",
                },
              },

              [`& .${PERIOD_CELL_ROW}`]: {
                "& div:last-of-type": {
                  borderRight: `0px`,
                  borderTopRightRadius: "15px",
                },
              },
            },
          }}
        >
          <Grid
            container
            wrap="nowrap"
            sx={{ width: "100%", "& p": { fontSize: "12px", color: theme.palette.text.disabled } }}
          >
            <Grid
              item
              sx={{ width: "20%", minWidth: "20%", flexGrow: 1 }}
              className={TABLE_CELL}
            >
              <Box></Box>
            </Grid>

            {dynamicColumn && (
              <Grid
                item
                sx={{ width: "10%" }}
                className={TABLE_CELL}
              >
                <Box
                  sx={{
                    height: "100px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px",
                    color: theme.palette.text.disabled,
                  }}
                >
                  {dynamicColumn}
                </Box>
              </Grid>
            )}

            {!!docsByPeriod.length && (
              <Grid
                item
                container
                wrap="nowrap"
                sx={{ flexGrow: docsByPeriod.length }}
                className={PERIOD_CELL_ROW}
              >
                {docsByPeriod.map(({ period }, idx) => {
                  return (
                    <React.Fragment key={idx}>
                      <Grid
                        item
                        xs
                        sx={{ flexGrow: 1 }}
                        className={TABLE_CELL}
                      >
                        <Box sx={{ height: "100px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <HeaderCellContent>
                            <Typography>{period}</Typography>
                            {(allowDownload || isSysAdmin) && (
                              <Button
                                variant="text"
                                disabled={!hasPeriodIds(items, period)}
                                onClick={() => handleDownloadDocsFiles(getPeriodIds(items, period))}
                              >
                                <FontAwesomeIcon icon={faDownload} />
                              </Button>
                            )}
                          </HeaderCellContent>
                        </Box>
                      </Grid>
                    </React.Fragment>
                  );
                })}
              </Grid>
            )}
          </Grid>
        </Box>
      )}
    </>
  );
};

export default DocumentsPeriod;
